/**
 * The single source of every URL this app knows about.
 *
 * Three groups:
 *   - `routes.*`        SPA paths, consumed by `react-router` links and navigations
 *   - `routes.api.*`    absolute REST paths into `apps/api`
 *   - `routes.sui.*`    outbound explorer links
 *
 * Nothing else in the app may contain a URL literal. Interpolated segments are
 * `encodeURIComponent`'d HERE, never at a call site, so an address containing a
 * `&` cannot forge a second query parameter.
 *
 * `routes.api.*` is imported only by `src/lib/api/*`; components call those
 * wrappers, never `fetch` directly.
 */
import type {
  CalculateLeasingRewardsQuery,
  CalculateUnlockedRewardsQuery,
  GetClaimsByUserAddressQuery,
  GetReferralCodeQuery,
  RewardsInfoQuery,
} from "@swarm/shared/schemas";

import { config } from "@/config";

/**
 * Origin every `routes.api.*` path is prefixed with.
 *
 * Empty by default, which means same-origin — the Vite dev proxy and the
 * production reverse proxy both mount `apps/api` under this app's origin, the
 * way the Next.js `app/api/**` handlers were. Set `VITE_API_URL` only when the
 * API genuinely lives on another host, and add that host to the API's
 * `CORS_ORIGIN`.
 */
export const API_BASE = config.apiBaseUrl;

/** Prefixes a REST path with `API_BASE`. Paths always start with `/`. */
const api = (path: string): string => `${API_BASE}${path}`;

/** Serializes a query object, skipping `undefined`, and encodes every value. */
const qs = (params: Record<string, string | number | boolean | undefined>): string => {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }

  const query = search.toString();

  return query ? `?${query}` : "";
};

export const routes = {
  /** Terminal login / referral capture. */
  home: () => `/`,

  dashboard: {
    index: () => `/dashboard`,
  },

  purchase: {
    index: () => `/purchase`,
  },

  referrals: {
    /** SPA path pattern for the router; `:code` is filled by react-router. */
    pattern: () => `/r/:code`,
    /** In-app path for a given code. */
    byCode: ({ code }: { code: string }) => `/r/${encodeURIComponent(code)}`,
    /**
     * Shareable ABSOLUTE link handed to a user to copy. Built from
     * `VITE_SITE_URL` because it leaves the app; the landing page reads `?code=`
     * and persists it before the wallet connects.
     */
    shareLink: ({ code }: { code: string }) => `${config.siteUrl}?code=${encodeURIComponent(code)}`,
  },

  api: {
    auth: {
      getJWTNonce: () => api(`/api/auth/get-jwt-nonce`),
    },

    referrals: {
      getReferralCodeByWalletAddress: ({ address }: GetReferralCodeQuery) =>
        api(`/api/referrals/get-referral-code-by-wallet-address${qs({ address })}`),
      /**
       * Ported from the retired referral-tree-info Cloudflare Worker. The Admin
       * Dashboard calls this cross-origin, so its origins have to be in the
       * API's `ALLOWED_ORIGINS`.
       */
      tree: ({ wallet, fullTree }: { wallet?: string; fullTree?: boolean }) =>
        api(`/api/referrals/tree${qs({ wallet, full_tree: fullTree ? "true" : undefined })}`),
      /**
       * Ported from the retired agent-license-referral-history Worker. `code` is
       * the encrypted code from a KOL link; passing an admin API key instead
       * returns the encrypted form of a plaintext code.
       */
      history: ({ code }: { code: string }) => api(`/api/referrals/history${qs({ code })}`),
    },

    rewards: {
      /** Ported from the retired get-rewards-info Worker. */
      info: ({ wallet }: RewardsInfoQuery) => api(`/api/rewards/info${qs({ wallet })}`),
    },

    claims: {
      dailyRewards: () => api(`/api/claims/daily-rewards`),
      scheduleClaim: () => api(`/api/claims/schedule-claim`),
      leasingRewards: () => api(`/api/claims/leasing-rewards`),
      calculateUnlockedRewards: ({ user_address }: CalculateUnlockedRewardsQuery) =>
        api(`/api/claims/calculate-unlocked-rewards${qs({ user_address })}`),
      calculateLeasingRewards: ({ user_address, lease_id }: CalculateLeasingRewardsQuery) =>
        api(`/api/claims/calculate-leasing-rewards${qs({ user_address, lease_id })}`),
      getByUserAddress: ({ user_address }: GetClaimsByUserAddressQuery) =>
        api(`/api/claims/get-by-user-address${qs({ user_address })}`),
    },

    licenseLeases: {
      requestLeaseCreation: () => api(`/api/license-leases/request-new-lease`),
      networkStats: () => api(`/api/license-leases/network-stats`),
      getDailyRewards: (owner: string, days?: number) =>
        api(`/api/license-leases/daily-rewards/${encodeURIComponent(owner)}${qs({ days })}`),
      getLeasesByOwner: (owner: string) =>
        api(`/api/license-leases/leases/${encodeURIComponent(owner)}`),
      getLicensesByOwner: (owner: string) =>
        api(`/api/license-leases/licenses/${encodeURIComponent(owner)}/periods`),
    },

    // `licenseHolder.score` and `draws.*` were removed on 2026-09-28 with the
    // draw feature: the score card rendered only inside a draw window, and the
    // last one closed on 2026-06-06. `/api/license-holder/score` still exists on
    // the API; nothing in this app calls it.
  },

  sui: {
    explorerUrl: ({ path }: { path: string }) => `${config.suiExplorerUrl}${path}`,
  },
} as const;

/**
 * Hook form of `routes`, kept because every ported call site uses it. It returns
 * the same frozen object — there is no per-render state — so calling it in a
 * component is free.
 */
export const useRoutes = () => routes;
